/**
 * ARCHIVE ORGANIZATION DIALOG
 *
 * Confirmation dialog shown before archiving an organization.
 * Displays the org name, active location count, and an access warning.
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Archive, AlertTriangle, Loader2, XCircle } from 'lucide-react';
import { archiveOrganization } from '@/lib/onboarding-status';
import { useAuth } from '@/hooks/use-auth';

interface ArchiveOrgDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  organizationName: string;
  activeLocationCount: number;
  onSuccess: () => void;
}

export function ArchiveOrgDialog({
  open,
  onOpenChange,
  organizationId,
  organizationName,
  activeLocationCount,
  onSuccess,
}: ArchiveOrgDialogProps) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleArchive = async () => {
    if (!user?.id) {
      setError('Unable to determine current user');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await archiveOrganization(organizationId, user.id);

      if (!result.success) {
        throw new Error(result.error ?? 'Archive failed');
      }

      onSuccess();
      onOpenChange(false);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to archive organization');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-md'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2 text-amber-700 dark:text-amber-400'>
            <Archive className='h-5 w-5' />
            Archive Organization
          </DialogTitle>
          <DialogDescription className='text-base font-medium text-gray-900 dark:text-gray-100 mt-1'>
            {organizationName}
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-4 py-2'>
          <p className='text-sm text-gray-700 dark:text-gray-300'>
            This will archive <strong>{organizationName}</strong>
            {activeLocationCount > 0 ? (
              <> and all of its{' '}
                <strong>{activeLocationCount} active location{activeLocationCount !== 1 ? 's' : ''}</strong>.
              </>
            ) : (
              <>. This organization has <strong>no active locations</strong>.</>
            )}
          </p>

          <div className='flex gap-3 rounded-md border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-950/30'>
            <AlertTriangle className='h-4 w-4 shrink-0 mt-0.5 text-amber-600 dark:text-amber-400' />
            <p className='text-sm text-amber-800 dark:text-amber-300'>
              {activeLocationCount > 0 ? (
                <>
                  All {activeLocationCount} location subscription{activeLocationCount !== 1 ? 's' : ''} will
                  be cancelled at period end. Users will lose access once each billing period expires.
                </>
              ) : (
                <>
                  <strong>This organization will be archived immediately</strong> — there are no active
                  subscriptions to cancel. This action takes effect right now.
                </>
              )}
            </p>
          </div>

          <p className='text-sm text-gray-600 dark:text-gray-400'>
            This action can be reversed from the <strong>Archived Organizations</strong> section.
          </p>

          {error && (
            <div className='flex gap-3 rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-950/30'>
              <XCircle className='h-4 w-4 shrink-0 mt-0.5 text-red-600 dark:text-red-400' />
              <p className='text-sm text-red-800 dark:text-red-300'>{error}</p>
            </div>
          )}
        </div>

        <DialogFooter className='gap-2'>
          <Button
            variant='outline'
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant='default'
            className='bg-amber-600 hover:bg-amber-700 text-white'
            onClick={handleArchive}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                Archiving…
              </>
            ) : (
              <>
                <Archive className='h-4 w-4 mr-2' />
                {activeLocationCount === 0 ? 'Archive Immediately' : 'Archive Organization'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
